/**
 * useKeyboardInset Hook
 *
 * Tracks the height (px) of the bottom region of the **layout** viewport that
 * is currently obscured by the on-screen software keyboard, using the
 * `visualViewport` API.
 *
 * Why this exists (review fix plan 4, Finding 4): a `position: sticky; bottom: 0`
 * (or `position: fixed; bottom: 0`) footer is anchored to the bottom of the
 * **layout** viewport. On iOS Safari / installed PWAs the on-screen keyboard
 * shrinks the **visual** viewport but leaves the layout viewport unchanged, so
 * such a footer floats *behind* the keyboard instead of resting directly above
 * it. The classic flex layout chat used previously sidestepped this; the
 * window-scroll restructure (Finding 2) re-introduced it for the sticky chat
 * composer. Consumers offset their footer (and any bottom-pinned fixed overlay
 * layer) by this inset so it tracks the visible viewport while the keyboard is
 * open.
 *
 * The inset is the gap between the bottom of the layout viewport and the bottom
 * of the visual viewport:
 *
 *   inset = window.innerHeight - visualViewport.height - visualViewport.offsetTop
 *
 * clamped to `>= 0`. With no keyboard the visual viewport fills the layout
 * viewport (`height === innerHeight`, `offsetTop === 0`) so the inset is `0` —
 * meaning desktop browsers and idle mobile both return `0` and the consumer's
 * layout is byte-for-byte unchanged. Self-gating this way is more robust than a
 * width breakpoint: desktop never reports a nonzero inset (it does not shrink
 * the visual viewport for a keyboard), while tablets wider than the mobile
 * breakpoint still get correct handling.
 *
 * Listens to BOTH `visualViewport` `'resize'` and `'scroll'`: iOS fires
 * `resize` when the keyboard animates in/out and `scroll` when the page is
 * nudged while the keyboard is open (e.g. focusing a field near the bottom).
 *
 * SSR-defensive: when `window` / `visualViewport` is unavailable the hook
 * returns `0` and skips listener registration, leaving the consumer's plain
 * `bottom: 0` behavior intact (the same as every non-keyboard environment).
 */
import { useEffect, useState } from 'react';

const computeKeyboardInset = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }
  const vv = window.visualViewport;
  if (!vv) {
    return 0;
  }
  const obscured = window.innerHeight - vv.height - vv.offsetTop;
  return obscured > 0 ? obscured : 0;
};

/**
 * @returns the current on-screen-keyboard inset in pixels (`0` when no keyboard
 *   is shown, on desktop, or when the `visualViewport` API is unavailable).
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState<number>(computeKeyboardInset);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const vv = window.visualViewport;
    const update = () => {
      const next = computeKeyboardInset();
      // Only re-render on an actual change so a stream of identical
      // `scroll`/`resize` events does not churn the consumer.
      setInset(prev => (prev !== next ? next : prev));
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}
